import React from 'react';
import { Box, Typography, Chip, LinearProgress, Stack } from '@mui/material';
import type { ExecuteEntity } from '@/types/run-stage.types';
import type { StepStats } from '@/run-stage/utils/stepFlattener';

interface RunStatusBarProps {
  execute: ExecuteEntity;
  stats: StepStats;
}

function formatTime(time?: string | null): string {
  if (!time) return '—';
  return new Date(time).toLocaleString();
}

function getStatusColor(execute: ExecuteEntity): 'success' | 'primary' | 'warning' | 'error' | 'default' {
  if (execute.completed) return 'success';
  const status = (execute as unknown as Record<string, unknown>).status;
  if (status === 'PAUSED') return 'warning';
  if (status === 'ABORTED') return 'error';
  if (execute.startTime) return 'primary';
  return 'default';
}

function getStatusLabel(execute: ExecuteEntity): string {
  if (execute.completed) return 'COMPLETED';
  const status = (execute as unknown as Record<string, unknown>).status;
  if (typeof status === 'string') return status;
  if (execute.startTime) return 'IN PROGRESS';
  return 'NOT STARTED';
}

export const RunStatusBar: React.FC<RunStatusBarProps> = ({ execute, stats }) => {
  const progress = stats.total > 0 ? ((stats.completed + stats.skipped) / stats.total) * 100 : 0;

  return (
    <Box
      sx={{
        px: 2,
        py: 1,
        backgroundColor: 'background.paper',
        borderBottom: '1px solid',
        borderColor: 'divider',
      }}
    >
      <Stack direction="row" spacing={3} sx={{ alignItems: 'center', mb: 0.5 }}>
        <Box>
          <Typography variant="caption" color="text.secondary">Start Time</Typography>
          <Typography variant="body2" sx={{ fontWeight: 500 }}>{formatTime(execute.startTime)}</Typography>
        </Box>
        <Chip
          label={getStatusLabel(execute)}
          color={getStatusColor(execute)}
          size="small"
          sx={{ fontWeight: 600 }}
        />
        <Box>
          <Typography variant="caption" color="text.secondary">End Time</Typography>
          <Typography variant="body2" sx={{ fontWeight: 500 }}>{formatTime(execute.endTime)}</Typography>
        </Box>
        <Box sx={{ flex: 1 }} />
        <Typography variant="caption" color="text.secondary">
          {Math.round(progress)}% complete
        </Typography>
      </Stack>
      <LinearProgress variant="determinate" value={progress} sx={{ borderRadius: 1, height: 4 }} />
    </Box>
  );
};
